import { useState, type ReactNode } from 'react';
import type { ContentFormat } from '../content-engine/opportunities';
import { CopyButton } from './CopyButton';

interface CopyFormatsGroupProps {
  formats: { id: ContentFormat; label: string }[];
  getText: (format: ContentFormat) => string;
  groupLabel: string;
  copyAriaLabel: (formatLabel: string) => string;
  buttonClassName?: string;
  children?: ReactNode;
}

/**
 * Boutons « Copier » par format avec aperçu : au clic, le texte copié
 * s'affiche sous les boutons pour que l'utilisateur voie exactement ce qui est
 * dans son presse-papiers. L'aperçu reste visible jusqu'au clic sur un autre
 * format. Remonter le composant avec une `key` quand le sujet change pour
 * fermer l'aperçu.
 */
export function CopyFormatsGroup({
  formats,
  getText,
  groupLabel,
  copyAriaLabel,
  buttonClassName,
  children,
}: CopyFormatsGroupProps) {
  const [previewId, setPreviewId] = useState<ContentFormat | null>(null);
  const preview = formats.find((format) => format.id === previewId) ?? null;
  return (
    <>
      <div className="ci-copy-group" role="group" aria-label={groupLabel}>
        <span className="ci-copy-group__label">Copier :</span>
        {formats.map((format) => (
          <CopyButton
            key={format.id}
            text={getText(format.id)}
            label={format.label}
            ariaLabel={copyAriaLabel(format.label)}
            className={buttonClassName}
            onCopy={() => setPreviewId(format.id)}
          />
        ))}
        {children}
      </div>
      {preview && (
        <div className="ci-copy-preview">
          <p className="ci-copy-preview__label">Texte copié au format {preview.label} :</p>
          <pre className="ci-content-card__body ci-opp-card__preview">{getText(preview.id)}</pre>
        </div>
      )}
    </>
  );
}
